import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Check, X } from 'lucide-react';
import { getEnvVar } from '../../../config/env';
import { PricingTierModal } from './PricingTierModal';

interface PricingTier {
  id: string;
  tier_name: string;
  tier_description?: string;
  
  // Subscription Pricing
  monthly_subscription_price?: number;
  monthly_subscription_price_usd?: number;
  included_credits_per_month?: number;
  credits_rollover_enabled?: boolean;
  display_currency?: string;
  
  // Feature Limits
  max_workspaces?: number;
  max_team_members?: number;
  max_contacts?: number;
  max_ai_employees?: number;
  max_whatsapp_instances?: number;
  max_voice_numbers?: number;
  storage_gb?: number;
  
  // Feature Flags
  email_support?: boolean;
  api_access_enabled?: boolean;
  white_label_enabled?: boolean;
  priority_support?: boolean;
  managed_service?: boolean;
  analytics_level?: string;
  integrations?: string[] | string;
  
  // Usage Pricing
  whatsapp_cost_per_message: number;
  voice_cost_per_minute: number;
  ai_task_cost_per_execution: number;
  email_cost_per_send: number;
  sms_cost_per_message: number;
  ai_usage_markup_percentage?: number;
  
  // Bulk Discount
  bulk_discount_threshold?: number;
  bulk_discount_percentage?: number;
  
  // Legacy fields (for backward compatibility)
  monthly_free_credits?: number;
  analytics_enabled?: boolean;
  
  // Status
  is_active: boolean;
  sort_order: number;
}

export const PricingTiersTab: React.FC = () => {
  const [tiers, setTiers] = useState<PricingTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingTier, setEditingTier] = useState<PricingTier | null>(null);

  const API_BASE_URL = getEnvVar('VITE_FASTAPI_BASE_URL');

  useEffect(() => {
    loadTiers();
  }, []);

  const loadTiers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/pricing/tiers?active_only=false`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to load pricing tiers');
      }

      const data = await response.json();
      // Handle both {success: true, data: [...]} and direct array responses
      const tiersData = data.success ? (data.data || []) : (Array.isArray(data) ? data : []);
      setTiers(tiersData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load pricing tiers');
      console.error('Error loading pricing tiers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingTier(null);
    setShowModal(true);
  };

  const handleEdit = (tier: PricingTier) => {
    setEditingTier(tier);
    setShowModal(true);
  };

  const handleDelete = async (tierId: string) => {
    if (!confirm('Are you sure you want to deactivate this pricing tier?')) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_auth_token');
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/pricing/tiers/${tierId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete pricing tier');
      }

      await loadTiers();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete pricing tier');
      console.error('Error deleting pricing tier:', err);
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingTier(null);
  };

  const handleModalSave = () => {
    loadTiers();
    handleModalClose();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header Actions */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Pricing Tiers</h2>
          <p className="text-sm text-gray-600 mt-1">
            Configure pricing tiers for different client segments
          </p>
        </div>
        <button
          onClick={handleCreate}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Tier
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Tiers Grid */}
      {tiers.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <p className="text-gray-500">No pricing tiers found. Create your first tier to get started.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {tiers.map((tier) => (
            <div
              key={tier.id}
              className={`bg-white rounded-lg border-2 p-6 ${
                tier.is_active ? 'border-gray-200' : 'border-gray-300 opacity-75'
              }`}
            >
              {/* Header */}
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 capitalize">
                    {tier.tier_name}
                  </h3>
                  {tier.tier_description && (
                    <p className="text-sm text-gray-600 mt-1">{tier.tier_description}</p>
                  )}
                </div>
                <span
                  className={`px-2 py-1 text-xs font-medium rounded-full ${
                    tier.is_active
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {tier.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>

              {/* Subscription Pricing */}
              {((tier.monthly_subscription_price !== undefined && tier.monthly_subscription_price > 0) ||
                (tier.monthly_subscription_price_usd !== undefined && tier.monthly_subscription_price_usd > 0)) && (
                <div className="mb-4 pb-4 border-b border-gray-200">
                  <div className="text-sm font-medium text-gray-900 mb-2">Monthly Price:</div>
                  <div className="flex flex-wrap gap-3">
                    {tier.monthly_subscription_price !== undefined && tier.monthly_subscription_price > 0 && (
                      <span className="text-lg font-bold text-blue-600">
                        ₹{tier.monthly_subscription_price.toLocaleString()}
                      </span>
                    )}
                    {tier.monthly_subscription_price_usd !== undefined && tier.monthly_subscription_price_usd > 0 && (
                      <span className="text-lg font-bold text-green-600">
                        ${tier.monthly_subscription_price_usd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    )}
                  </div>
                  {tier.included_credits_per_month !== undefined && tier.included_credits_per_month > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Included Credits:</span>
                      <span className="font-medium">
                        {tier.included_credits_per_month.toLocaleString()}
                        {tier.credits_rollover_enabled && (
                          <span className="text-green-600 ml-1">(rollover)</span>
                        )}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Feature Limits */}
              <div className="mb-4 pb-4 border-b border-gray-200">
                <div className="text-xs font-medium text-gray-500 mb-2">Feature Limits</div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {tier.max_workspaces !== undefined && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Workspaces:</span>
                      <span className="font-medium">{tier.max_workspaces === -1 ? '∞' : tier.max_workspaces}</span>
                    </div>
                  )}
                  {tier.max_team_members !== undefined && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Team:</span>
                      <span className="font-medium">{tier.max_team_members === -1 ? '∞' : tier.max_team_members}</span>
                    </div>
                  )}
                  {tier.max_contacts !== undefined && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Contacts:</span>
                      <span className="font-medium">{tier.max_contacts === -1 ? '∞' : tier.max_contacts.toLocaleString()}</span>
                    </div>
                  )}
                  {tier.max_ai_employees !== undefined && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">AI Employees:</span>
                      <span className="font-medium">{tier.max_ai_employees === -1 ? '∞' : tier.max_ai_employees}</span>
                    </div>
                  )}
                  {tier.storage_gb !== undefined && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Storage:</span>
                      <span className="font-medium">{tier.storage_gb}GB</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Usage Pricing */}
              <div className="space-y-2 text-sm mb-4">
                <div className="text-xs font-medium text-gray-500 mb-2">Usage Pricing</div>
                <div className="flex justify-between">
                  <span className="text-gray-600">WhatsApp:</span>
                  <span className="font-medium">₹{tier.whatsapp_cost_per_message.toFixed(2)}/msg</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Voice:</span>
                  <span className="font-medium">₹{tier.voice_cost_per_minute.toFixed(2)}/min</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">AI Task:</span>
                  <span className="font-medium">
                    ₹{tier.ai_task_cost_per_execution.toFixed(2)}/task
                    {tier.ai_usage_markup_percentage && (
                      <span className="text-xs text-gray-500 ml-1">(+{tier.ai_usage_markup_percentage}%)</span>
                    )}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Email:</span>
                  <span className="font-medium">₹{tier.email_cost_per_send.toFixed(2)}/email</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">SMS:</span>
                  <span className="font-medium">₹{tier.sms_cost_per_message.toFixed(2)}/SMS</span>
                </div>
                {tier.bulk_discount_threshold && tier.bulk_discount_percentage && (
                  <div className="flex justify-between pt-2 border-t border-gray-100">
                    <span className="text-gray-600">Bulk Discount:</span>
                    <span className="font-medium">
                      {tier.bulk_discount_percentage}% (≥{tier.bulk_discount_threshold})
                    </span>
                  </div>
                )}
              </div>

              {/* Features */}
              <div className="mb-4 pt-4 border-t border-gray-100">
                <div className="text-xs font-medium text-gray-500 mb-2">Features</div>
                <div className="flex flex-wrap gap-2">
                  {tier.analytics_level && tier.analytics_level !== 'none' && (
                    <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded capitalize">
                      {tier.analytics_level} Analytics
                    </span>
                  )}
                  {tier.email_support && (
                    <span className="px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded">Email Support</span>
                  )}
                  {tier.api_access_enabled && (
                    <span className="px-2 py-1 text-xs bg-purple-100 text-purple-800 rounded">API</span>
                  )}
                  {tier.white_label_enabled && (
                    <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded">White Label</span>
                  )}
                  {tier.priority_support && (
                    <span className="px-2 py-1 text-xs bg-orange-100 text-orange-800 rounded">Priority</span>
                  )}
                  {tier.managed_service && (
                    <span className="px-2 py-1 text-xs bg-indigo-100 text-indigo-800 rounded">Managed</span>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex space-x-2 pt-4 border-t border-gray-100">
                <button
                  onClick={() => handleEdit(tier)}
                  className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <Edit2 className="h-4 w-4 mr-1" />
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(tier.id)}
                  className="px-3 py-2 border border-red-300 shadow-sm text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <PricingTierModal
          tier={editingTier}
          onClose={handleModalClose}
          onSave={handleModalSave}
        />
      )}
    </div>
  );
};
